import React, { useState, useCallback, useEffect } from 'react'
import {
  Drawer,
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material'
import { Image } from 'react-bootstrap'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'
import DragAndDrop from '@/components/UI/DragAndDrop/DragAndDrop'
import TipBox from '@/components/UI/TipBox'
import { HelperService } from '@/services/helper.service'
import RoleplayMediaService from '@/services/RoleplayMediaService'
import { getAgentFromStorage } from '@/utils/appUtils'
import { cn } from '@/lib/utils'
import { validateVideoUrlComprehensive } from '@/utils/mediaValidation'
import mp4Icon from '@/assets/images/file-mp4.svg'
import closeimg from '@/assets/New-images/close-icon-red.svg'

const helperService = new HelperService()
const roleplayMediaService = new RoleplayMediaService()

interface VideoDrawerProps {
  open: boolean
  onClose: () => void
  onSave: (videoData: {
    file?: File
    id?: string
    ids?: string[]
    title?: string
    fileNames?: string[] // Array of file names for multiple uploads
    urlNames?: string[] // Array of URL names for multiple URLs
  }) => void
  onDelete?: () => void // Callback to delete entire video from editor
  initialData?: { id?: string; ids?: string[]; title?: string }
}

const VideoDrawer: React.FC<VideoDrawerProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  initialData,
}) => {
  const { t } = useTranslation('translation')
  const [activeTab, setActiveTab] = useState<'upload' | 'url'>('upload')
  const [videoFiles, setVideoFiles] = useState<File[]>([])
  const [videoUrls, setVideoUrls] = useState<string[]>([])
  const [videoUrl, setVideoUrl] = useState('')
  const [urlError, setUrlError] = useState<string>('')
  const [saving, setSaving] = useState(false)
  const [validatingUrl, setValidatingUrl] = useState(false)
  const [badgeLabel, setBadgeLabel] = useState<string>('') // Display name for the video
  // State to store fetched media info for each ID
  const [mediaInfoMap, setMediaInfoMap] = useState<
    Record<
      string,
      {
        file_name: string
        file_size: number
        mime_type: string
        url: string
      }
    >
  >({})
  const [loadingMediaInfo, setLoadingMediaInfo] = useState(false)
  // State for replacing existing video
  const [replacingId, setReplacingId] = useState<string | null>(null)
  const [replacementFile, setReplacementFile] = useState<File | null>(null)
  const [replacementPreview, setReplacementPreview] = useState<string | null>(
    null,
  )
  const [isDirty, setIsDirty] = useState(false) // Track if user has modified list in Edit mode

  const isEditMode = !!(initialData?.ids?.length || initialData?.id)

  // Load initial data when drawer opens
  useEffect(() => {
    if (open && initialData) {
      // Set display name from initialData
      setBadgeLabel(initialData.title || '')

      if (initialData.ids && initialData.ids.length > 0) {
        setVideoUrls(initialData.ids)
        // Check first item to determine tab
        const firstItem = initialData.ids[0]
        const isUrl =
          firstItem.startsWith('http') || firstItem.startsWith('blob:')
        setActiveTab(isUrl ? 'url' : 'upload')

        setVideoUrl('')
        setVideoFiles([])
      } else if (initialData.id) {
        setVideoUrls([initialData.id])
        // Check item to determine tab
        const isUrl =
          initialData.id.startsWith('http') ||
          initialData.id.startsWith('blob:')
        setActiveTab(isUrl ? 'url' : 'upload')

        setVideoUrl('')
        setVideoFiles([])
      } else {
        setVideoUrls([])
        setVideoUrl('')
        setVideoFiles([])
      }
      // Reset replacement states
      setReplacementPreview(null)
      setIsDirty(false)
    } else if (open && !initialData) {
      // Reset when opening for new video
      setBadgeLabel('')
      setVideoUrls([])
      setVideoUrl('')
      setVideoFiles([])
      setActiveTab('upload')
      setMediaInfoMap({})
      setReplacingId(null)
      setReplacementFile(null)
      setReplacementPreview(null)
      setIsDirty(false)
    }
  }, [open, initialData])

  // Fetch media info for videoUrls (existing video IDs)
  useEffect(() => {
    const fetchMediaInfo = async () => {
      if (videoUrls.length === 0) {
        setMediaInfoMap({})
        return
      }

      setLoadingMediaInfo(true)
      const newMediaInfoMap: typeof mediaInfoMap = {}

      for (const id of videoUrls) {
        // Skip if already fetched
        if (mediaInfoMap[id]) {
          newMediaInfoMap[id] = mediaInfoMap[id]
          continue
        }

        try {
          const response = await roleplayMediaService.getMediaInfo(id)
          // Try both possible response structures
          const media =
            response?.data?.data?.media || response?.data?.media || null

          if (media) {
            newMediaInfoMap[id] = {
              file_name: media.file_name || id,
              file_size: media.file_size || 0,
              mime_type: media.mime_type || 'video/mp4',
              url: media.url || roleplayMediaService.getStreamUrl(id),
            }
          } else {
            // Fallback if API fails
            newMediaInfoMap[id] = {
              file_name: id,
              file_size: 0,
              mime_type: 'video/mp4',
              url: roleplayMediaService.getStreamUrl(id),
            }
          }
        } catch (error) {
          // Fallback on error
          newMediaInfoMap[id] = {
            file_name: id,
            file_size: 0,
            mime_type: 'video/mp4',
            url: roleplayMediaService.getStreamUrl(id),
          }
        }
      }

      setMediaInfoMap(newMediaInfoMap)
      setLoadingMediaInfo(false)
    }

    fetchMediaInfo()
  }, [videoUrls])

  const tipMessage = t(
    'Tip: Fill out the AI Assistant Role and Learner Role fields first to create a more specific scenario.',
  )

  const isYouTubeUrl = (url: string): boolean => {
    return /(?:youtube\.com|youtu\.be)/i.test(url)
  }

  const isVimeoUrl = (url: string): boolean => {
    return /vimeo\.com/i.test(url)
  }

  const getYouTubeEmbedUrl = (url: string): string | null => {
    const match = url.match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/i,
    )
    if (match && match[1]) {
      return `https://www.youtube.com/embed/${match[1]}`
    }
    return null
  }

  const getVimeoEmbedUrl = (url: string): string | null => {
    const match = url.match(
      /vimeo\.com\/(?:channels\/[^/]+\/|groups\/[^/]+\/videos\/|)(\d+)/i,
    )
    if (match && match[1]) {
      return `https://player.vimeo.com/video/${match[1]}`
    }
    return null
  }

  const getFileIcon = (fileName: string): string => {
    // Always return MP4 icon for video files
    return mp4Icon
  }

  const handleFileDrop = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return

      const validFiles = files.filter(file => file.type === 'video/mp4')

      if (validFiles.length === 0) {
        helperService.showToaster(
          t('The file type you are trying to upload is not supported.'),
          'error',
        )
        return
      }

      // In Edit Mode, first addition replaces existing content
      if (isEditMode && !isDirty) {
        setVideoUrls([])
        setVideoFiles(validFiles)
        setIsDirty(true)
      } else {
        setVideoFiles(prev => [...prev, ...validFiles])
      }
    },
    [t, isEditMode, isDirty],
  )

  const handleAddUrl = async () => {
    if (!videoUrl.trim()) {
      helperService.showToaster(t('Please enter a video URL'), 'error')
      return
    }

    const trimmedUrl = videoUrl.trim()

    // Check for duplicates
    if (videoUrls.includes(trimmedUrl)) {
      helperService.showToaster(t('This URL has already been added'), 'error')
      return
    }

    // Validate URL format and accessibility
    setValidatingUrl(true)
    setUrlError('')

    try {
      const validation = await validateVideoUrlComprehensive(trimmedUrl, t)
      if (!validation.isValid) {
        setUrlError(validation.error || t('Invalid video URL'))
        helperService.showToaster(
          validation.error || t('Invalid video URL'),
          'error',
        )
        setValidatingUrl(false)
        return
      }

      // URL is valid, add to list
      // In Edit Mode, first addition replaces existing content
      if (isEditMode && !isDirty) {
        setVideoUrls([trimmedUrl])
        setVideoFiles([])
        setIsDirty(true)
      } else {
        setVideoUrls(prev => [...prev, trimmedUrl])
      }
      setVideoUrl('')
      setUrlError('')
      helperService.showToaster(t('Video URL added successfully'), 'success')
    } catch (error) {
      helperService.showToaster(t('Failed to validate video URL'), 'error')
      setUrlError(t('Failed to validate video URL'))
    } finally {
      setValidatingUrl(false)
    }
  }

  const handleRemoveUrl = (index: number) => {
    setVideoUrls(prev => prev.filter((_, i) => i !== index))
  }

  const handleRemoveVideo = (index: number) => {
    setVideoFiles(prev => prev.filter((_, i) => i !== index))
  }

  // Handler to start replacing an existing video
  const handleStartReplace = (videoId: string) => {
    setReplacingId(videoId)
    setReplacementFile(null)
    setReplacementPreview(null)
  }

  // Handler for replacement file drop
  const handleReplacementFileDrop = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return

      const videoFile = files.find(file => file.type.startsWith('video/'))
      if (!videoFile) {
        helperService.showToaster(
          t('The file type you are trying to upload is not supported.'),
          'error',
        )
        return
      }

      setReplacementFile(videoFile)
      // Create preview URL
      const previewUrl = URL.createObjectURL(videoFile)
      setReplacementPreview(previewUrl)
    },
    [t],
  )

  // Handler to cancel replacement
  const handleCancelReplacement = () => {
    if (replacementPreview) {
      URL.revokeObjectURL(replacementPreview)
    }
    setReplacingId(null)
    setReplacementFile(null)
    setReplacementPreview(null)
  }

  // Handler to confirm replacement and call updateMedia API
  const handleConfirmReplacement = async () => {
    if (!replacingId || !replacementFile) return

    try {
      setSaving(true)
      const formData = new FormData()
      formData.append('file', replacementFile)
      formData.append('filepath', replacementFile.name)

      const response = await roleplayMediaService.updateMedia(
        replacingId,
        formData,
      )
      const responseData = response?.data
      const status = responseData?.status
      let statusCode: number | undefined

      if (status != null && status !== undefined) {
        if (typeof status === 'object') {
          const statusObj = status as { code?: number } | null
          if (
            statusObj &&
            'code' in statusObj &&
            typeof statusObj.code === 'number'
          ) {
            statusCode = statusObj.code
          }
        } else if (typeof status === 'number') {
          statusCode = status
        }
      }

      if (statusCode === 200) {
        helperService.showToaster(t('Video updated successfully'), 'success')
        // Update mediaInfoMap with new info
        const media = responseData?.data?.media
        if (media) {
          setMediaInfoMap(prev => ({
            ...prev,
            [replacingId]: {
              file_name: media.file_name || replacementFile.name,
              file_size: media.file_size || replacementFile.size,
              mime_type: media.mime_type || replacementFile.type,
              url: media.url || '',
            },
          }))
        }
        // Reset replacement state
        if (replacementPreview) {
          URL.revokeObjectURL(replacementPreview)
        }
        setReplacingId(null)
        setReplacementFile(null)
        setReplacementPreview(null)
      } else {
        throw new Error(responseData?.message || t('Failed to update video'))
      }
    } catch (error: any) {
      helperService.showToaster(
        error?.message || t('Failed to update video'),
        'error',
      )
    } finally {
      setSaving(false)
    }
  }

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  const handleSave = async () => {
    // If editing with URLs, allow saving IDs/URLs directly even in upload tab
    if (activeTab === 'upload') {
      if (videoUrls.length > 0 && videoFiles.length === 0) {
        await onSave({ ids: videoUrls, id: videoUrls[0], title: badgeLabel })
        onClose()
        return
      }

      if (videoFiles.length === 0) {
        helperService.showToaster(t('Please upload a video'), 'error')
        return
      }
    }

    if (activeTab === 'url') {
      if (videoUrls.length === 0) {
        helperService.showToaster(
          t('Please add at least one video URL'),
          'error',
        )
        return
      }
    }

    try {
      setSaving(true)

      if (activeTab === 'upload' && videoFiles.length > 0) {
        // Upload first file to server
        const formData = new FormData()
        formData.append('file', videoFiles[0])
        formData.append('filepath', videoFiles[0].name)

        const response = await roleplayMediaService.uploadVideo(formData)

        // Response format from ApiUtil (AxiosResponse):
        // response.data = { data: { media: { url, ... } }, status: { code: 200, message: null } }
        // So we need: response.data.data.media.url
        let statusCode: number | undefined
        let statusMessage: string | null = null

        const responseData = response?.data
        const status = responseData?.status
        if (status != null && status !== undefined) {
          if (typeof status === 'object') {
            // TypeScript guard: after typeof check, status is object | null, so check null again
            const statusObj = status as {
              code?: number
              message?: string | null
            } | null
            if (
              statusObj &&
              'code' in statusObj &&
              typeof statusObj.code === 'number'
            ) {
              statusCode = statusObj.code
              statusMessage = statusObj.message || null
            }
          } else if (typeof status === 'number') {
            statusCode = status
          }
        }

        if (statusCode === 200 && responseData?.data?.media?._id) {
          const videoId = responseData.data.media._id

          // If multiple files uploaded, collect all IDs and file names
          if (videoFiles.length > 1) {
            const allIds: string[] = [videoId]
            const fileNames: string[] = [videoFiles[0].name] // Store file names

            for (const file of videoFiles.slice(1)) {
              const formData = new FormData()
              formData.append('file', file)
              formData.append('filepath', file.name)

              const res = await roleplayMediaService.uploadVideo(formData)
              const resData = res?.data
              const resStatus = resData?.status
              let resStatusCode: number | undefined

              if (resStatus != null && resStatus !== undefined) {
                if (typeof resStatus === 'object') {
                  const statusObj = resStatus as { code?: number } | null
                  if (
                    statusObj &&
                    'code' in statusObj &&
                    typeof statusObj.code === 'number'
                  ) {
                    resStatusCode = statusObj.code
                  }
                } else if (typeof resStatus === 'number') {
                  resStatusCode = resStatus
                }
              }

              if (resStatusCode === 200 && resData?.data?.media?._id) {
                allIds.push(resData.data.media._id)
              }
            }

            // Use badgeLabel if provided, otherwise use first file name
            const title = badgeLabel.trim() || fileNames[0]
            await onSave({
              ids: allIds,
              title,
              file: videoFiles[0],
              fileNames: fileNames,
            })
          } else {
            // Single file - use badgeLabel if provided, otherwise use file name
            const title = badgeLabel.trim() || videoFiles[0].name
            await onSave({ id: videoId, title, file: videoFiles[0] })
          }
        } else {
          throw new Error(
            statusMessage ||
              responseData?.message ||
              t('Failed to upload video'),
          )
        }
      } else if (activeTab === 'url' && videoUrls.length > 0) {
        // Use full URLs as names (will be truncated if too long in display)
        const urlNames = videoUrls.map(url => url)
        // Use badgeLabel if provided, otherwise use first URL
        const title = badgeLabel.trim() || urlNames[0]
        await onSave({ ids: videoUrls, title, urlNames: urlNames })
      }

      // Reset state
      setVideoFiles([])
      setVideoUrls([])
      setVideoUrl('')
      setActiveTab('upload')
      onClose()
    } catch (error: any) {
      helperService.showToaster(
        error?.message || t('Failed to save video'),
        'error',
      )
    } finally {
      setSaving(false)
    }
  }

  const handleClose = () => {
    if (!saving) {
      setVideoFiles([])
      setVideoUrls([])
      setVideoUrl('')
      setUrlError('')
      setActiveTab('upload')
      onClose()
    }
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 420, md: 503 },
          maxWidth: '100%',
          boxSizing: 'border-box',
          background: '#FFFFFF',
          border: '1px solid #ECEFF7',
          borderRadius: '6px',
        },
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            pb: 2,
            borderBottom: '1px solid #ECEFF7',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {isEditMode ? t('Edit Video') : t('Upload Video')}
          </Typography>
          <IconButton onClick={handleClose} size="small" disabled={saving}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Tabs */}
        <Box
          sx={{ borderBottom: 1, borderColor: 'divider', px: { xs: 2, md: 3 } }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            aria-label="video upload tabs"
          >
            <Tab label={t('Upload Video')} value="upload" />
            <Tab label={t('Import from URL')} value="url" />
          </Tabs>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: { xs: 2, md: 3 },
          }}
        >
          {/* Shared elements - Display Name and TipBox */}
          <Stack spacing={2}>
            {/* Tip Box Display */}
            <TipBox message={tipMessage} />

            {/* Name Input - Only show in edit mode */}
            {isEditMode && (
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  {t('Name')}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t(
                    'Enter a name for this video (e.g., "Training Video", "Demo")',
                  )}
                  value={badgeLabel}
                  onChange={e => setBadgeLabel(e.target.value)}
                  disabled={saving}
                  helperText={t(
                    'This name will be shown in the editor to identify this video',
                  )}
                />
              </Box>
            )}

            {/* Upload Tab Content */}
            {activeTab === 'upload' && (
              <>
                {/* Upload Area - Only show when NOT editing existing videos */}
                {videoUrls.length === 0 && videoFiles.length === 0 && (
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      minHeight: { xs: 200, sm: 220, md: 240 },
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        '& .custom-file-wrap': {
                          width: '100% !important',
                          height: '100% !important',
                          display: 'flex !important',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minHeight: '180px',
                        },
                      }}
                    >
                      <DragAndDrop
                        getFileDocumentsHandler={handleFileDrop}
                        supportedTypes=".mp4"
                        maxFiles={100}
                        message={`<h2 className='mb-1'>${t('Drop your files here or click to browse.')}</h2><div>${t('Supported File types: .Mp4')}</div>`}
                        className={cn(
                          saving ? 'tw-opacity-50 tw-pointer-events-none' : '',
                          'tw-w-full',
                        )}
                      />
                    </Box>
                  </Box>
                )}

                {/* Selected Files List */}
                {videoFiles.length > 0 && (
                  <div className="selected-files mt-3 w-100">
                    {videoFiles.map((file, index) => (
                      <div
                        key={index}
                        className="files d-flex align-items-center border-grey radius-4 px-3 py-2 mb-2"
                      >
                        <div className="d-flex align-items-center tw-w-full">
                          <Image src={getFileIcon(file.name)} />
                          <div className="ml-3 mr-5 font-sm truncate-document tw-flex-1">
                            <strong>{file.name}</strong>
                            <span className="font-12 clr-black font-600 tw-block">
                              {formatBytes(file.size)}
                            </span>
                          </div>
                          <div className="ml-auto w-auto d-flex align-items-center">
                            {!saving && (
                              <button
                                className="btn p-0 ml-3 btn-red-close"
                                onClick={() => handleRemoveVideo(index)}
                              >
                                <Image src={closeimg} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Current Videos Preview (for editing existing) */}
                {videoUrls.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      {t('Preview Video')}
                    </Typography>
                    {loadingMediaInfo ? (
                      <Box className="tw-text-center tw-py-4 tw-text-gray-500">
                        {t('Loading...')}
                      </Box>
                    ) : (
                      videoUrls.map((videoId, index) => {
                        const isRawUrl =
                          videoId.startsWith('http') ||
                          videoId.startsWith('blob:')
                        const mediaInfo = mediaInfoMap[videoId]
                        const fileName = isRawUrl
                          ? videoId
                          : mediaInfo?.file_name || videoId
                        const fileSize = isRawUrl
                          ? 0
                          : mediaInfo?.file_size || 0
                        const videoUrl = isRawUrl
                          ? videoId
                          : mediaInfo?.url || ''

                        return (
                          <Box
                            key={index}
                            sx={{ position: 'relative', width: '100%', mb: 2 }}
                          >
                            {videoUrl && (
                              <Box
                                sx={{
                                  position: 'relative',
                                  width: '100%',
                                  borderRadius: '8px',
                                  overflow: 'hidden',
                                  backgroundColor: '#000',
                                }}
                              >
                                {isYouTubeUrl(videoUrl) ? (
                                  <iframe
                                    src={
                                      getYouTubeEmbedUrl(videoUrl) || videoUrl
                                    }
                                    title="YouTube video"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    style={{
                                      width: '100%',
                                      height: 220,
                                      border: 'none',
                                      display: 'block',
                                    }}
                                  />
                                ) : isVimeoUrl(videoUrl) ? (
                                  <iframe
                                    src={getVimeoEmbedUrl(videoUrl) || videoUrl}
                                    title="Vimeo video"
                                    allow="autoplay; fullscreen; picture-in-picture"
                                    allowFullScreen
                                    style={{
                                      width: '100%',
                                      height: 220,
                                      border: 'none',
                                      display: 'block',
                                    }}
                                  />
                                ) : (
                                  <video
                                    src={videoUrl}
                                    controls
                                    style={{
                                      width: '100%',
                                      maxHeight: 220,
                                      display: 'block',
                                    }}
                                  />
                                )}
                                <IconButton
                                  onClick={() => handleRemoveUrl(index)}
                                  disabled={saving}
                                  sx={{
                                    position: 'absolute',
                                    top: 12,
                                    right: 12,
                                    backgroundColor: '#FFFFFF',
                                    borderRadius: '50%',
                                    width: 36,
                                    height: 36,
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                    '&:hover': {
                                      backgroundColor: '#FFFFFF',
                                      transform: 'scale(1.05)',
                                    },
                                  }}
                                >
                                  <CloseIcon
                                    sx={{ fontSize: 20, color: '#666' }}
                                  />
                                </IconButton>
                              </Box>
                            )}
                            {videoUrl && (
                              <Box sx={{ mt: 1 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 500,
                                    wordBreak: 'break-all',
                                  }}
                                >
                                  {fileName}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {formatBytes(fileSize)}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )
                      })
                    )}
                  </Box>
                )}
              </>
            )}

            {/* URL Tab Content */}
            {activeTab === 'url' && (
              <>
                {/* URL Input - Hide if videos exist */}
                {videoUrls.length === 0 && (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                      {t('Video URL')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder={t(
                          'https://youtube.com/watch?v=... or https://vimeo.com/...',
                        )}
                        value={videoUrl}
                        onChange={e => {
                          setVideoUrl(e.target.value)
                          if (urlError) setUrlError('')
                        }}
                        onKeyPress={e => {
                          if (e.key === 'Enter' && !validatingUrl) {
                            e.preventDefault()
                            handleAddUrl()
                          }
                        }}
                        disabled={saving || validatingUrl}
                        error={!!urlError}
                        helperText={
                          validatingUrl
                            ? t('Validating URL...')
                            : urlError ||
                              t(
                                'Supports YouTube, Vimeo, or direct video URLs (MP4)',
                              )
                        }
                      />
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleAddUrl}
                        disabled={saving || validatingUrl || !videoUrl.trim()}
                        sx={{ alignSelf: 'flex-start', whiteSpace: 'nowrap' }}
                      >
                        {validatingUrl ? t('Validating...') : t('Add Link')}
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Video Preview */}
                {videoUrls.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      {t('Preview Video')}
                    </Typography>
                    {loadingMediaInfo ? (
                      <Box className="tw-text-center tw-py-4 tw-text-gray-500">
                        {t('Loading...')}
                      </Box>
                    ) : (
                      videoUrls.map((videoId, index) => {
                        const isRawUrl =
                          videoId.startsWith('http') ||
                          videoId.startsWith('blob:')
                        const mediaInfo = mediaInfoMap[videoId]
                        const fileName = isRawUrl
                          ? videoId
                          : mediaInfo?.file_name || videoId
                        const fileSize = isRawUrl
                          ? 0
                          : mediaInfo?.file_size || 0
                        const videoUrl = isRawUrl
                          ? videoId
                          : mediaInfo?.url || ''

                        return (
                          <Box
                            key={index}
                            sx={{ position: 'relative', width: '100%', mb: 2 }}
                          >
                            {videoUrl && (
                              <Box
                                sx={{
                                  position: 'relative',
                                  width: '100%',
                                  borderRadius: '8px',
                                  overflow: 'hidden',
                                  backgroundColor: '#000',
                                }}
                              >
                                {isYouTubeUrl(videoUrl) ? (
                                  <iframe
                                    src={
                                      getYouTubeEmbedUrl(videoUrl) || videoUrl
                                    }
                                    title="YouTube video"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    style={{
                                      width: '100%',
                                      height: 220,
                                      border: 'none',
                                      display: 'block',
                                    }}
                                  />
                                ) : isVimeoUrl(videoUrl) ? (
                                  <iframe
                                    src={getVimeoEmbedUrl(videoUrl) || videoUrl}
                                    title="Vimeo video"
                                    allow="autoplay; fullscreen; picture-in-picture"
                                    allowFullScreen
                                    style={{
                                      width: '100%',
                                      height: 220,
                                      border: 'none',
                                      display: 'block',
                                    }}
                                  />
                                ) : (
                                  <video
                                    src={videoUrl}
                                    controls
                                    style={{
                                      width: '100%',
                                      maxHeight: 220,
                                      display: 'block',
                                    }}
                                  />
                                )}
                                <IconButton
                                  onClick={() => handleRemoveUrl(index)}
                                  disabled={saving}
                                  sx={{
                                    position: 'absolute',
                                    top: 12,
                                    right: 12,
                                    backgroundColor: '#FFFFFF',
                                    borderRadius: '50%',
                                    width: 36,
                                    height: 36,
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                    '&:hover': {
                                      backgroundColor: '#FFFFFF',
                                      transform: 'scale(1.05)',
                                    },
                                  }}
                                >
                                  <CloseIcon
                                    sx={{ fontSize: 20, color: '#666' }}
                                  />
                                </IconButton>
                              </Box>
                            )}
                            {videoUrl && (
                              <Box sx={{ mt: 1 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 500,
                                    wordBreak: 'break-all',
                                  }}
                                >
                                  {fileName}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {formatBytes(fileSize)}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )
                      })
                    )}
                  </Box>
                )}
              </>
            )}
          </Stack>
        </Box>

        {/* Footer */}
        {/* Footer */}
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            borderTop: '1px solid #ECEFF7',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          {/* Delete button - only show in edit mode */}
          {isEditMode && onDelete ? (
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                onDelete()
                onClose()
              }}
              disabled={saving}
            >
              {t('Delete')}
            </Button>
          ) : (
            <Box /> // Spacer
          )}

          <Stack direction="row" spacing={2}>
            <Button variant="outlined" onClick={handleClose} disabled={saving}>
              {t('Cancel')}
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving
                ? isEditMode
                  ? t('Updating...')
                  : activeTab === 'upload'
                    ? t('Uploading...')
                    : t('Linking...')
                : isEditMode
                  ? t('Update Video')
                  : activeTab === 'upload'
                    ? t('Upload Video')
                    : t('Link Video')}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Drawer>
  )
}

export default VideoDrawer
